import {
  Client,
  ClientsFilter,
  useUpdateSminexClubClientTicket,
  mapClientsEventToTableRows,
  INVITATION_STATUSES,
  UpdateSminexClubClientTicketInput,
  RECONFIRMATION_STATUSES,
  useSetSminexClubGuests,
  AdditionalGuestUpdate,
} from '@entities/clients';
import { DeleteParticipantModal } from '@features/clients';
import { useFetchDeleteTicket } from '@features/api/use-delete-tickets';
import { DataTable } from '@shared/ui/data-table';
import { useMemo, useRef, useState } from 'react';
import { PRESENCE_FILTER_ALL, PRESENCE_FILTER_PRESENT_ONLY } from './consts';
import { buildEventClientsTableColumns } from './columns/buildEventClientsTableColumns';
import { useClientTableView } from './hooks/useClientTableView';
import { ManagerFilterPayload } from './lib';
import { useToast } from '@shared/hooks/useToast';
import { patchClientTicket } from '@shared/api/sminex-club-clients-ws';
import { refetchEventPageWs } from '@shared/api/event-page-ws';
import styles from './EventClientsTable.module.scss';

interface ParticipantToDelete {
  ticketId: number;
  participantName: string;
}

interface EventClientsTableProps {
  clients: Client[];
  allClients?: Client[];
  isLoading: boolean;
  eventId: number;
  handleOrderChange: (order: string[] | null) => void;
  handleFilterChange: (value: ClientsFilter) => void;
  refetchClients: () => void;
  dateStart?: string;
}

export const EventClientsTable = ({
  clients,
  allClients,
  isLoading,
  eventId,
  handleOrderChange,
  handleFilterChange,
  refetchClients,
  dateStart,
}: EventClientsTableProps) => {
  const baseRows = useMemo(() => mapClientsEventToTableRows(clients), [clients]);
  const filterSourceRows = useMemo(() => mapClientsEventToTableRows(allClients ?? clients), [allClients, clients]);
  const [openFilterField, setOpenFilterField] = useState<string | null>(null);
  const [selectedProjectsFilter, setSelectedProjectsFilter] = useState<string[]>([]);
  const [selectedInvitationStatuses, setSelectedInvitationStatuses] = useState<string[]>([]);
  const [selectedReconfirmationStatuses, setSelectedReconfirmationStatuses] = useState<string[]>([]);
  const [selectedPresenceFilter, setSelectedPresenceFilter] = useState<string>(PRESENCE_FILTER_ALL);
  const [participantToDelete, setParticipantToDelete] = useState<ParticipantToDelete | null>(null);
  const { updateSminexClubClientTicket } = useUpdateSminexClubClientTicket();
  const { setSminexClubGuests } = useSetSminexClubGuests();
  const { deleteTickets } = useFetchDeleteTicket();
  const { showToast } = useToast();
  const savingTicketFieldsRef = useRef(new Set<string>());

  const {
    rows,
    filterItems,
    clientFilters,
    applyBrokersFilter,
    applyManagerFilter,
    selectCallsFilter,
    handleSortChange,
  } = useClientTableView(baseRows, handleOrderChange, filterSourceRows);

  const applyClientsFilter = ({
    projectName = selectedProjectsFilter,
    invitationStatus = selectedInvitationStatuses,
    reconfirmationStatus = selectedReconfirmationStatuses,
    presence = selectedPresenceFilter,
  }: {
    projectName?: string[];
    invitationStatus?: string[];
    reconfirmationStatus?: string[];
    presence?: string;
  } = {}) => {
    handleFilterChange({
      projectName: projectName.length ? projectName : null,
      invitationStatus:
        invitationStatus.length && invitationStatus.length !== INVITATION_STATUSES.length ? invitationStatus : null,
      reconfirmationStatus:
        reconfirmationStatus.length && reconfirmationStatus.length !== RECONFIRMATION_STATUSES.length
          ? reconfirmationStatus
          : null,
      checked: presence === PRESENCE_FILTER_ALL ? null : presence === PRESENCE_FILTER_PRESENT_ONLY,
      manager: null,
    });
  };

  const saveTicketField = async (ticketId: number, input: UpdateSminexClubClientTicketInput) => {
    const fieldKey = Object.keys(input)
      .sort((left, right) => left.localeCompare(right))
      .join(',');
    const saveKey = `${ticketId}:${fieldKey}`;

    if (savingTicketFieldsRef.current.has(saveKey)) return;

    savingTicketFieldsRef.current.add(saveKey);

    try {
      const result = await updateSminexClubClientTicket(ticketId, input);
      const savedTicketId = result.data?.updateSminexClubClientTicket?.id;

      if (result.error || !savedTicketId) {
        showToast('Не удалось сохранить изменения', 'error');
        return;
      }

      patchClientTicket(ticketId, input);

      if (eventId) {
        refetchEventPageWs(eventId, { silent: true });
      }
    } finally {
      savingTicketFieldsRef.current.delete(saveKey);
    }
  };

  const onChangeInvitation = (ticketId: number, sminexClubInvitation: string) => {
    void saveTicketField(ticketId, { sminexClubInvitation });
  };

  const onChangeReconfirmationStatus = (ticketId: number, sminexClubReconfirmationStatus: string) => {
    void saveTicketField(ticketId, { sminexClubReconfirmationStatus });
  };

  const onChangeReview = (ticketId: number, review: string) => {
    void saveTicketField(ticketId, { review });
  };

  const onChangeComment = (ticketId: number, comment: string) => {
    void saveTicketField(ticketId, { comment });
  };

  const onChangeMobileComment = (ticketId: number, mobileComment: string) => {
    void saveTicketField(ticketId, { mobileComment });
  };

  const onChangeSpecial = (ticketId: number, isSpecial: boolean) => {
    void saveTicketField(ticketId, { isSpecial });
  };

  const onChangeAdditionalGuest = async (ticketId: number, value: AdditionalGuestUpdate[]) => {
    try {
      const result = await setSminexClubGuests(ticketId, value);

      if (result.error) {
        showToast('Не удалось сохранить изменения', 'error');
        return;
      }

      if (eventId) {
        refetchEventPageWs(eventId, { silent: true });
      }
    } catch {
      showToast('Не удалось сохранить изменения', 'error');
    }
  };

  const onChangeInvitingManager = (ticketId: number, invitedByManager: string) => {
    void saveTicketField(ticketId, { invitedByManager });
  };

  const onDeleteClient = (ticketId: number, participantName: string) => {
    setParticipantToDelete({ ticketId, participantName });
  };

  const onCloseDeleteModal = () => {
    setParticipantToDelete(null);
  };

  const onConfirmDeleteClient = async () => {
    if (!participantToDelete) return;

    await deleteTickets({ ticketIds: [participantToDelete.ticketId] });
    refetchClients();
    onCloseDeleteModal();
  };

  const onApplyProjectsFilter = (selectedProjects: string[]) => {
    setSelectedProjectsFilter(selectedProjects);
    applyClientsFilter({ projectName: selectedProjects });
  };

  const onApplyInvitationFilter = (value: string[]) => {
    setSelectedInvitationStatuses(value);
    applyClientsFilter({ invitationStatus: value });
    setOpenFilterField(null);
  };

  const onApplyReconfirmationStatusFilter = (value: string[]) => {
    setSelectedReconfirmationStatuses(value);
    applyClientsFilter({ reconfirmationStatus: value });
    setOpenFilterField(null);
  };

  const onSelectPresenceFilter = (value: string) => {
    setSelectedPresenceFilter(value);
    applyClientsFilter({ presence: value });
    setOpenFilterField(null);
  };

  const onApplyBrokersFilter = (value: string[]) => {
    applyBrokersFilter(value);
  };

  const onApplyManagerFilter = (payload: ManagerFilterPayload) => {
    applyManagerFilter(payload);
    applyClientsFilter();
  };

  const onSelectCallsFilter = (value: string) => {
    selectCallsFilter(value);
    setOpenFilterField(null);
  };

  const selectedRows = useMemo(() => rows.filter(({ isSpecial }) => isSpecial).map(({ id }) => id), [rows]);

  const columns = buildEventClientsTableColumns({
    dateStart,
    openFilterField,
    setOpenFilterField,
    serverFilters: {
      selectedProjectsFilter,
      selectedInvitationStatuses,
      selectedReconfirmationStatuses,
      selectedPresenceFilter,
    },
    clientFilters,
    filterItems,
    filterHandlers: {
      onApplyProjectsFilter,
      onApplyInvitationFilter,
      onApplyReconfirmationStatusFilter,
      onSelectPresenceFilter,
      onApplyBrokersFilter,
      onApplyManagerFilter,
      onSelectCallsFilter,
    },
    cellHandlers: {
      onChangeInvitation,
      onChangeReconfirmationStatus,
      onChangeReview,
      onChangeComment,
      onChangeInvitingManager,
      onDeleteClient,
      onChangeAdditionalGuest,
      onChangeMobileComment,
      onChangeSpecial,
    },
  });

  return (
    <>
      <DataTable
        rows={rows}
        columns={columns}
        loading={isLoading}
        sortingMode="server"
        multiSort
        onSortChange={handleSortChange}
        tableClassName={styles.table}
        scrollable
        selectedRows={selectedRows}
        selectedClassName={styles.selectedRow}
      />

      <DeleteParticipantModal
        isOpen={!!participantToDelete}
        onClose={onCloseDeleteModal}
        onConfirm={onConfirmDeleteClient}
        participantName={participantToDelete?.participantName ?? ''}
      />
    </>
  );
};
